import { IUser } from '../../types/auth.types';
import {
  getUserWorkspaces,
  type WorkspaceId,
} from '../../config/workspaces';

type Props = {
  user: IUser;
  selectedWorkspaceId: WorkspaceId;
  onWorkspaceSelect: (workspaceId: WorkspaceId) => void;
  creatorOnboardingComplete?: boolean | null;
};

const WORKSPACE_ORDER: Record<WorkspaceId, number> = {
  buyer: 0,
  creator: 1,
  seller: 2,
  owner: 3,
  staff: 4,
};

/** Soft selected tab — persona tint, no hard border (matches nav accent system). */
const WORKSPACE_TAB_STYLES: Record<
  WorkspaceId,
  { active: string; idle: string; glow: string }
> = {
  buyer: {
    active:
      'bg-gradient-to-br from-[#3B3B98]/14 via-purple-50 to-white text-[#3B3B98]',
    idle: 'text-slate-600 hover:bg-white hover:text-slate-900',
    glow: 'shadow-[0_2px_16px_rgba(59,59,152,0.16)]',
  },
  seller: {
    active:
      'bg-gradient-to-br from-violet-500/12 via-violet-50 to-white text-violet-800',
    idle: 'text-slate-600 hover:bg-white hover:text-slate-900',
    glow: 'shadow-[0_2px_16px_rgba(124,58,237,0.14)]',
  },
  creator: {
    active:
      'bg-gradient-to-br from-fuchsia-500/12 via-fuchsia-50 to-white text-fuchsia-800',
    idle: 'text-slate-600 hover:bg-white hover:text-slate-900',
    glow: 'shadow-[0_2px_16px_rgba(217,70,239,0.14)]',
  },
  owner: {
    active:
      'bg-gradient-to-br from-indigo-500/12 via-indigo-50 to-white text-indigo-800',
    idle: 'text-slate-600 hover:bg-white hover:text-slate-900',
    glow: 'shadow-[0_2px_16px_rgba(79,70,229,0.14)]',
  },
  staff: {
    active:
      'bg-gradient-to-br from-indigo-500/12 via-indigo-50 to-white text-indigo-800',
    idle: 'text-slate-600 hover:bg-white hover:text-slate-900',
    glow: 'shadow-[0_2px_16px_rgba(79,70,229,0.14)]',
  },
};

export function WorkspacesSwitcher({
  user,
  selectedWorkspaceId,
  onWorkspaceSelect,
  creatorOnboardingComplete,
}: Readonly<Props>) {
  const workspaces = [...getUserWorkspaces(user, { creatorOnboardingComplete })].sort(
    (a, b) => WORKSPACE_ORDER[a.id] - WORKSPACE_ORDER[b.id],
  );

  if (workspaces.length <= 1) return null;

  return (
    <div className="border-b border-slate-100 px-3 py-3">
      <p className="px-0.5 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
        Workspaces
      </p>
      <div
        className="flex gap-1 rounded-xl bg-slate-100 p-1"
        role="tablist"
        aria-label="Workspaces"
      >
        {workspaces.map((ws) => {
          const active = ws.id === selectedWorkspaceId;
          const styles = WORKSPACE_TAB_STYLES[ws.id];
          return (
            <button
              key={ws.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onWorkspaceSelect(ws.id)}
              title={ws.description}
              className={`min-w-0 flex-1 rounded-lg px-2 py-2 text-center text-[11px] font-semibold leading-tight transition-all duration-200 ease-out sm:text-xs ${
                active
                  ? `${styles.active} ${styles.glow}`
                  : styles.idle
              }`}
            >
              <span className={`line-clamp-2 ${active ? 'font-semibold' : 'font-medium'}`}>
                {ws.label}
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-2 px-0.5 text-[11px] text-slate-500">
        Switch workspace, then choose a page below.
      </p>
    </div>
  );
}
